// App.js - Main application component.
// Serves as the app root, rendering NavBar (full width), routed content, and Footer.
// Provides layout for the invoice generator with responsiveness, global state (via GlobalProvider), i18n (via I18nextProvider), and routing (via BrowserRouter).
// Key features: View switching (form/history) on home route, theme/language effects, PageLayout for consistent layouts, ScrollToTop for UX, and dynamic metadata updates via React Helmet.
// Updated: Added HelmetProvider for async head management and DynamicMetadata component to dynamically update HTML lang, title, meta descriptions, Open Graph, Twitter, and structured data based on current language (EN/ES).
import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import InvoiceForm from './components/InvoiceForm';
import NavBar from './components/NavBar';
import InvoiceHistory from './components/InvoiceHistory';
import Footer from './components/Footer';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import PageLayout from './components/PageLayout';
import ScrollToTop from './components/ScrollToTop';
import { GlobalProvider } from './contexts/GlobalContext';
import { useGlobalContext } from './hooks/useGlobalContext';
import './i18n';
import { I18nextProvider } from 'react-i18next';
import i18n from 'i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async'; // Added for dynamic head management
import { useTranslation } from 'react-i18next'; // Added for accessing translations in DynamicMetadata

// DynamicMetadata: Component to handle dynamic updates to document head based on current language.
// Uses useTranslation to fetch translated strings and updates <html lang>, <title>, meta tags, and structured data.
// Re-renders on language change to reflect EN/ES metadata.
const DynamicMetadata = () => {
  const { t, i18n } = useTranslation(); // Access translations and current language
  const currentLang = i18n.language; // Get current language code (e.g., 'en' or 'es')

  return (
    <Helmet>
      {/* Dynamically set the html lang attribute */}
      <html lang={currentLang} />
      
      {/* Primary SEO Meta - Dynamic title and description */}
      <title>{t('metadata.title')}</title>
      <meta name="description" content={t('metadata.description')} />
      
      {/* Open Graph - Dynamic titles and descriptions */}
      <meta property="og:title" content={t('metadata.ogTitle')} />
      <meta property="og:description" content={t('metadata.ogDescription')} />
      
      {/* Twitter Card - Dynamic titles and descriptions */}
      <meta name="twitter:title" content={t('metadata.twitterTitle')} />
      <meta name="twitter:description" content={t('metadata.twitterDescription')} />
      
      {/* Structured Data - Update the JSON-LD script dynamically */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": t('metadata.structuredDataName'),
          "url": "https://invoicegenerator.codeqazone.com/",
          "description": t('metadata.structuredDataDescription'),
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser",
          "author": {
            "@type": "Person",
            "name": "ypdev19",
            "jobTitle": "Software Engineer",
            "url": "https://www.codeqazone.com/",
            "sameAs": [
              "https://github.com/ypdev19",
              "https://stackoverflow.com/users/18516849/ypdev19",
              "https://coderlegion.com/user/ypdev19"
            ]
          },
          "publisher": {
            "@type": "Person",
            "name": "ypdev19"
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        })}
      </script>
    </Helmet>
  );
};

// AppContent: Handles main logic and rendering within providers.
// Manages currentView for form/history toggle on home route, applies theme/language effects, and wraps routes in PageLayout.
// Updated: Added DynamicMetadata component to update head tags dynamically based on language.
const AppContent = () => {
  const [currentView, setCurrentView] = useState('form'); // Toggles between form and history views on home route
  const { theme, language } = useGlobalContext();

  // Applies theme globally via Bootstrap's data-bs-theme
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  // Updates i18n language on change
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  // Google Analytics GA4 Initialization
  useEffect(() => {
    const MEASUREMENT_ID = 'G-Y76CG2W6JS';

    // Prevent duplicate injection during hot-reloading or re-renders
    if (!document.querySelector(`script[src*="${MEASUREMENT_ID}"]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      gtag('js', new Date());
      gtag('config', MEASUREMENT_ID, {
        send_page_view: true
      });
    }
  }, []); // Empty dependency array ensures this runs exactly once on mount

  return (
    <>
      <DynamicMetadata /> {/* Dynamically updates metadata based on current language */}
      <NavBar currentView={currentView} setCurrentView={setCurrentView} /> {/* Renders on all pages */}
      <Routes>
        {/* Home: Conditionally renders InvoiceForm or InvoiceHistory based on currentView, wrapped in PageLayout */}
        <Route
          path="/"
          element={
            <PageLayout>
              {currentView === 'form' ? (
                <InvoiceForm />
              ) : (
                <InvoiceHistory setCurrentView={setCurrentView} />
              )}
            </PageLayout>
          }
        />
        {/* Terms of Service: Wrapped in PageLayout for consistency */}
        <Route path="/terms-of-service" element={<PageLayout><TermsOfService /></PageLayout>} />
        {/* Privacy Policy: Wrapped in PageLayout for consistency */}
        <Route path="/privacy-policy" element={<PageLayout><PrivacyPolicy /></PageLayout>} />
      </Routes>
      <Footer /> {/* Renders on all pages */}
    </>
  );
};

// App: Root component wrapping the app with providers for global state, i18n, routing, and head management.
// Updated: Added HelmetProvider to enable dynamic head updates.
const App = () => {
  return (
    <HelmetProvider> {/* Wraps the app for async head management with React Helmet */}
      <I18nextProvider i18n={i18n}>
        <GlobalProvider>
          <Router>
            <ScrollToTop /> {/* Resets scroll on route changes */}
            <AppContent />
          </Router>
        </GlobalProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
};

export default App;